import React from 'react';
import { SpeakerAvatar, DescriptionBlock } from '@components/landing/SpeakerCardV2';
import { Speaker } from '@graphql/gql/graphql';
import Modal from '@cvent/carina/components/Modal';
import { CSSObject } from '@emotion/react';
import DismissButton from '@cvent/carina/components/ScrollViewWithBars/DismissButton';
import useBlockTheme from '@cvent/blocks/hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import { ModalFormat } from '@cvent/carina/components/Modal/Modal';
import { injectTestId } from '@cvent/nucleus-test-automation';
import ScrollViewWithBars from '@cvent/carina/components/ScrollViewWithBars/ScrollViewWithBars';
import { Z_INDEX } from '@components/shared/zIndex';

export type SpeakerModalTypes = {
  testID?: string;
  speaker: Pick<Speaker, 'biography' | 'firstName' | 'lastName' | 'profileImageUri' | 'title' | 'company'>;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
};

const useStyles = (): Record<string, CSSObject> => {
  const { font } = useBlockTheme();
  const { mediaIsMobile, mediaIsM, mediaIsDefaultSize } = useBreakpoints();

  return {
    headerContainer: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'row',
      padding: '16px 24px',
      gap: 0,
      [mediaIsDefaultSize]: {
        flexDirection: 'column'
      },
      [mediaIsMobile]: {
        gap: 16,
        marginRight: 0,
        padding: '16px 0'
      },
      [mediaIsM]: {
        marginBottom: 24,
        padding: 16
      }
    },
    closeButton: {
      paddingTop: 8
    },
    biographyContainer: {
      color: font.color.base,
      padding: '16px 24px',
      fontSize: font.base.size[6],
      [mediaIsMobile]: {
        padding: 0,
        fontSize: font.base.size[2]
      },
      [mediaIsM]: {
        padding: 16
      }
    },
    contentContainer: {
      justifyContent: 'space-between',
      alignSelf: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      [mediaIsM]: {
        gap: 18
      },
      paddingBottom: 24,
      overflowWrap: 'anywhere'
    },
    modal: {
      position: 'relative',
      padding: `24px 24px 0 24px`,
      [mediaIsMobile]: {
        padding: 16
      },
      [mediaIsM]: {
        padding: 16
      }
    }
  };
};

export function SpeakerModal({
  speaker,
  testID = 'speaker-modal',
  setIsModalOpen,
  isModalOpen
}: SpeakerModalTypes): JSX.Element {
  const { headerContainer, avatarContainer, contentContainer, closeButton, biographyContainer, modal } = useStyles();
  const { isM, isMobile, heightWindow } = useBreakpoints();

  let modalSize: ModalFormat = 'l';
  let avatarSize = 9.4;
  if (isM) {
    modalSize = 'm';
    avatarSize = 6;
  }
  if (isMobile) {
    modalSize = 's';
    avatarSize = 4;
  }

  return (
    <Modal
      format={modalSize}
      isOpen={isModalOpen}
      tabIndex={0}
      css={modal}
      onDismiss={() => setIsModalOpen(false)}
      zIndex={Z_INDEX.MODAL_ON_TOP_OF_LAYOUT_HEADER}
    >
      <ScrollViewWithBars
        css={contentContainer}
        forceStickyHeader
        header={
          <div css={headerContainer}>
            <div css={avatarContainer}>
              <SpeakerAvatar
                testID={testID}
                firstName={speaker.firstName}
                lastName={speaker.lastName}
                profileImageUri={speaker.profileImageUri}
                avatarSize={avatarSize}
              />
              <DescriptionBlock
                firstName={speaker.firstName}
                lastName={speaker.lastName}
                title={speaker.title}
                company={speaker.company}
                testID={testID}
              />
            </div>
            <div css={closeButton}>
              <DismissButton onClick={() => setIsModalOpen(false)} {...injectTestId(`${testID}-dismiss-button`)} />
            </div>
          </div>
        }
        {...{ style: { maxHeight: heightWindow - 60 } }}
      >
        <div css={biographyContainer}>{speaker.biography}</div>
      </ScrollViewWithBars>
    </Modal>
  );
}

export default SpeakerModal;
